import html2canvas from "html2canvas";
import jsPDF from "jspdf";

export class Pdf {
    yOffset: number;
    pageWidth: number;
    pageHeight: number;
    padding: number;
    pdf: jsPDF;

    constructor(
        pageWidth: number,
        pageHeight: number,
        padding: number,
        pdf: jsPDF
    ) {
        this.yOffset = padding;
        this.pageWidth = pageWidth;
        this.pageHeight = pageHeight;
        this.padding = padding;
        this.pdf = pdf;
    }

    async addElements(
        elements: NodeListOf<Element>,
        options: { allowSlicing?: boolean; startNewPage?: boolean; rowSelector?: string } = {}
    ) {
        const { allowSlicing = true, startNewPage = false, rowSelector = '.MuiDataGrid-row, .inspection-grid-row' } = options;

        const layoutContent = document.querySelector<HTMLElement>('[data-layout-content]');
        const originalLayoutMarginLeft = layoutContent ? layoutContent.style.marginLeft : '';
        const originalLayoutMarginPriority = layoutContent ? layoutContent.style.getPropertyPriority('margin-left') : '';

        if (layoutContent) {
            layoutContent.style.setProperty('margin-left', '66px', 'important');
            await new Promise<void>((resolve) => setTimeout(resolve, 150));
        }

        try {
            if (startNewPage && this.yOffset > this.padding) {
                this.pdf.addPage();
                this.yOffset = this.padding;
            }

            for (let i = 0; i < elements.length; i++) {
                const element = elements[i] as HTMLElement;
                const label = element.id ? '#' + element.id : 'element ' + i;

                // The page stays LIVE while this runs. A capture at scale 2 takes a moment per
                // element, and any re-render in between -- a MobX update, the video's playhead,
                // a list that finished saving -- replaces the very nodes this NodeList points at.
                // html2canvas clones from the document root and finds its target by walking that
                // clone, so a detached node is simply never reached and the cloner throws
                // "Unable to find element in cloned iframe". That used to abort the export with
                // an uncaught rejection: no PDF, no message, and the button still spinning.
                if (!element.isConnected) {
                    console.warn('PDF export: skipped ' + label + ', no longer in the document.');
                    continue;
                }

                let canvas: HTMLCanvasElement;
                try {
                    canvas = await html2canvas(element, {
                        scale: 2,
                        useCORS: true,
                        scrollY: -window.scrollY,
                    });
                } catch (error) {
                    // Detached mid-capture, or unrenderable for some other reason. Losing one
                    // section beats losing the document -- and the warning names which section.
                    console.warn('PDF export: could not render ' + label + '.', error);
                    continue;
                }

                const imgWidth = this.pageWidth - 2 * this.padding;
                const mmPerPx = imgWidth / canvas.width;
                const totalImgHeightMm = canvas.height * mmPerPx;
                const maxContentY = this.pageHeight - this.padding;
                const usableHeightMm = maxContentY - this.padding;

                if (this.yOffset + totalImgHeightMm <= maxContentY) {
                    const imgData = canvas.toDataURL('image/png');
                    this.pdf.addImage(imgData, 'PNG', this.padding, this.yOffset, imgWidth, totalImgHeightMm);
                    this.yOffset += totalImgHeightMm + 5;
                    continue;
                }

                if (!allowSlicing || totalImgHeightMm <= usableHeightMm) {
                    this.pdf.addPage();
                    this.yOffset = this.padding;
                    const imgData = canvas.toDataURL('image/png');
                    this.pdf.addImage(imgData, 'PNG', this.padding, this.yOffset, imgWidth, totalImgHeightMm);
                    this.yOffset += totalImgHeightMm + 5;
                    continue;
                }

                const elementRect = element.getBoundingClientRect();
                const pxScale = canvas.height / elementRect.height;
                const rowBottomsPx: number[] = [];
                element.querySelectorAll(rowSelector).forEach((row) => {
                    const r = (row as HTMLElement).getBoundingClientRect();
                    const bottomPx = Math.round((r.bottom - elementRect.top) * pxScale);
                    if (bottomPx > 0 && bottomPx <= canvas.height) rowBottomsPx.push(bottomPx);
                });
                rowBottomsPx.sort((a, b) => a - b);

                let cursorPx = 0;
                while (cursorPx < canvas.height) {
                    let availableMm = maxContentY - this.yOffset;
                    if (availableMm <= 0) {
                        this.pdf.addPage();
                        this.yOffset = this.padding;
                        availableMm = maxContentY - this.yOffset;
                    }

                    const remainingPx = canvas.height - cursorPx;
                    const maxSlicePx = Math.max(1, Math.floor(availableMm / mmPerPx));
                    let slicePx = Math.min(remainingPx, maxSlicePx);

                    if (cursorPx + slicePx < canvas.height) {
                        const candidates = rowBottomsPx.filter(
                            (y) => y > cursorPx && y <= cursorPx + slicePx
                        );
                        if (candidates.length > 0) {
                            slicePx = candidates[candidates.length - 1] - cursorPx;
                        }
                    }

                    const sliceCanvas = document.createElement('canvas');
                    sliceCanvas.width = canvas.width;
                    sliceCanvas.height = slicePx;
                    const ctx = sliceCanvas.getContext('2d');
                    if (ctx) {
                        ctx.drawImage(
                            canvas,
                            0, cursorPx, canvas.width, slicePx,
                            0, 0, canvas.width, slicePx
                        );
                    }

                    const sliceHeightMm = slicePx * mmPerPx;
                    this.pdf.addImage(
                        sliceCanvas.toDataURL('image/png'),
                        'PNG',
                        this.padding,
                        this.yOffset,
                        imgWidth,
                        sliceHeightMm
                    );

                    cursorPx += slicePx;
                    this.yOffset += sliceHeightMm;

                    if (cursorPx < canvas.height) {
                        this.pdf.addPage();
                        this.yOffset = this.padding;
                    }
                }

                this.yOffset += 5;
            }
        } finally {
            if (layoutContent) {
                layoutContent.style.removeProperty('margin-left');
                if (originalLayoutMarginLeft) {
                    layoutContent.style.setProperty('margin-left', originalLayoutMarginLeft, originalLayoutMarginPriority);
                }
            }
        }
    }

    save(filename: string) {
        this.pdf.save(filename);
    }
}
